<template>
  <div class="cps-wrap">

    <!-- ══════════════════════════════════════════════════
         HERO CARD — Current Pack
    ══════════════════════════════════════════════════ -->
    <div class="cps-hero" :style="heroStyle">
      <!-- decorative blobs -->
      <div class="cps-blob cps-blob--1" :style="{ background: blobColor }"></div>
      <div class="cps-blob cps-blob--2" :style="{ background: blobColor }"></div>

      <!-- Top row: label + upgrade btn -->
      <div class="cps-hero__top">
        <div class="cps-hero__badge">
          <v-icon size="12" :color="t.color">mdi-star-four-points</v-icon>
          <span>แพ็คของฉัน</span>
        </div>
        <v-btn
          small depressed
          class="cps-upgrade-btn"
          :style="upgradeStyle"
          @click="openDialog"
        >
          <v-icon left x-small>mdi-crown</v-icon>
          อัปเกรด
        </v-btn>
      </div>

      <!-- Main: icon + name -->
      <div class="cps-hero__main">
        <div class="cps-hero__icon-ring" :style="{ background: iconRingBg }">
          <v-icon :color="t.color" size="32">{{ t.icon }}</v-icon>
        </div>
        <div>
          <div class="cps-hero__name" :style="{ color: t.textDark }">
            {{ packName }}
          </div>
          <div class="cps-hero__desc" :style="{ color: t.textMid }">
            {{ currentPack ? currentPack.description || 'แพ็คปัจจุบันของคุณ' : 'แพ็คเริ่มต้น — ฟรี' }}
          </div>
        </div>
      </div>

      <!-- Tier pills -->
      <div class="cps-hero__tiers">
        <div
          v-for="(tier, i) in displayTiers"
          :key="i"
          class="cps-tier-pill"
          :style="tierPillStyle"
        >
          <span class="cps-tier-pill__gen">{{ tier.tierName || ('G' + (i + 1)) }}</span>
          <span class="cps-tier-pill__sep" :style="{ color: t.color }">·</span>
          <span class="cps-tier-pill__rate" :style="{ color: t.color }">{{ tier.rate }}%</span>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════
         UPGRADE DIALOG
    ══════════════════════════════════════════════════ -->
    <v-dialog v-model="dialog" max-width="700px" scrollable>
      <v-card class="rounded-xl" elevation="0" style="border:1px solid #f1f5f9">

        <!-- Dialog Header -->
        <div class="cpd-head">
          <div class="d-flex align-center" style="gap:10px">
            <div class="cpd-head__icon">
              <v-icon color="#F4811F" size="20">mdi-crown</v-icon>
            </div>
            <div>
              <div class="cpd-head__title">เลือกแพ็คคอมมิชชัน</div>
              <div class="cpd-head__sub">ปลดล็อคการรับค่าคอมมิชชันจากสายงานของคุณ</div>
            </div>
          </div>
          <v-btn icon small @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <v-divider />

        <v-card-text class="pa-4" style="max-height:520px">
          <v-progress-circular v-if="loadingPacks" indeterminate color="#F4811F" class="d-block mx-auto my-8" size="36" />

          <v-row v-else dense>
            <v-col v-for="p in packs" :key="p.id" cols="12" sm="6">
              <div
                class="cpd-pack-card"
                :class="{
                  'cpd-pack-card--active':   isCurrentPack(p.id),
                  'cpd-pack-card--selected': selectedPackId === p.id && !isCurrentPack(p.id),
                  'cpd-pack-card--locked':   isLowerPack(p),
                }"
                @click="onPackClick(p)"
              >
                <!-- Card header row -->
                <div class="cpd-pack-card__head">
                  <div class="d-flex align-center" style="gap:8px">
                    <div class="cpd-pack-icon" :style="{ background: theme(p.name).bg }">
                      <v-icon :color="theme(p.name).color" size="18">{{ theme(p.name).icon }}</v-icon>
                    </div>
                    <span class="cpd-pack-name" :style="{ color: theme(p.name).text }">{{ p.name }}</span>
                    <v-chip v-if="isCurrentPack(p.id)" x-small label color="#fff7ed" text-color="#F4811F" class="font-weight-bold">ปัจจุบัน</v-chip>
                    <v-chip v-else-if="isLowerPack(p)" x-small label color="#f1f5f9" text-color="#64748b" class="font-weight-bold">
                      <v-icon left x-small>mdi-lock</v-icon>ดาวน์เกรดไม่ได้
                    </v-chip>
                  </div>
                  <div class="d-flex align-center" style="gap:4px">
                    <v-icon v-if="selectedPackId === p.id && !isCurrentPack(p.id)" color="#F4811F" size="18">mdi-check-circle</v-icon>
                    <span class="cpd-pack-price" :style="{ color: p.price === 0 ? '#10b981' : '#F4811F' }">
                      {{ p.price === 0 ? 'ฟรี' : '฿' + p.price.toLocaleString() }}
                    </span>
                  </div>
                </div>

                <!-- Description -->
                <div class="cpd-pack-desc">{{ p.description }}</div>

                <!-- Tiers -->
                <div class="cpd-pack-tiers">
                  <span
                    v-for="t in p.tiers"
                    :key="t.id"
                    class="cpd-tier-chip"
                    :style="{ background: theme(p.name).bg, color: theme(p.name).text, borderColor: theme(p.name).color + '44' }"
                  >
                    {{ t.tierName }} <b>{{ t.rate }}%</b>
                  </span>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider />

        <!-- Dialog footer -->
        <v-card-actions class="cpd-footer">
          <div v-if="selectedPack && selectedPack.price > 0" class="cpd-footer__balance">
            <v-icon size="15" color="#64748b" class="mr-1">mdi-wallet-outline</v-icon>
            <span v-if="fetchingBalance" class="grey--text text--lighten-1">กำลังโหลด...</span>
            <template v-else>
              กระเป๋า <strong class="mx-1">฿{{ effectiveBalance.toLocaleString() }}</strong>
              <span class="cpd-footer__dot">·</span>
              ราคา
              <strong class="ml-1" :class="effectiveBalance < selectedPack.price ? 'error--text' : 'success--text'">
                ฿{{ selectedPack.price.toLocaleString() }}
              </strong>
            </template>
          </div>
          <v-spacer />
          <v-btn text @click="dialog = false" class="font-weight-bold" style="text-transform:none">ยกเลิก</v-btn>
          <v-btn
            color="#F4811F" dark depressed
            class="rounded-lg font-weight-bold px-5"
            style="text-transform:none"
            :disabled="!canBuy || fetchingBalance"
            :loading="buying || fetchingBalance"
            @click="confirmBuy"
          >
            <v-icon left small>mdi-crown</v-icon>
            {{ selectedPack && selectedPack.price === 0 ? 'เลือกแพ็คนี้' : 'ซื้อแพ็คนี้' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import { getPublicPacksApi, buyCommissionPackApi } from "@/services/api/commissionServices";
import swalAlert from "@/services/alert/swalServices";
import { packTheme } from "@/utils/packTheme";
import WalletService from "@/services/api/wallet";

// gradient map ต่อ pack
const GRADIENTS = {
  "Aurora":       ["#D1FAE5", "#A7F3D0", "#6EE7B7"],
  "The Moon":     ["#F1F5F9", "#E2E8F0", "#CBD5E1"],
  "The Sun":      ["#FEF9C3", "#FEF08A", "#FDE047"],
  "The Star":    ["#DBEAFE", "#BFDBFE", "#93C5FD"],
  "The Galaxy":   ["#EDE9FE", "#DDD6FE", "#C4B5FD"],
  "Supercluster": ["#FEF2E8", "#FDE8D0", "#FDCAA0"],
};

function gradient(name) {
  const g = GRADIENTS[name] || GRADIENTS["Aurora"];
  return `linear-gradient(135deg, ${g[0]} 0%, ${g[1]} 55%, ${g[2]} 100%)`;
}

export default {
  name: "CommissionPackSection",

  props: {
    currentPack:   { type: Object, default: null },
    walletBalance: { type: Number, default: 0 },
    role:          { type: String, default: "user" },
  },

  data() {
    return {
      dialog: false,
      packs: [],
      loadingPacks: false,
      selectedPackId: null,
      buying: false,
      liveBalance: null,
      fetchingBalance: false,
    };
  },

  computed: {
    packName() {
      return this.currentPack ? this.currentPack.name : "Aurora";
    },
    t() {
      return packTheme(this.packName);
    },
    heroStyle() {
      return { background: gradient(this.packName) };
    },
    blobColor() {
      return this.t.color + "22";
    },
    iconRingBg() {
      return "rgba(255,255,255,0.65)";
    },
    upgradeStyle() {
      return {
        background: "rgba(255,255,255,0.85)",
        color: this.t.color,
        border: `1.5px solid ${this.t.color}44`,
      };
    },
    tierPillStyle() {
      return {
        background: "rgba(255,255,255,0.7)",
        border: `1px solid ${this.t.color}44`,
      };
    },
    displayTiers() {
      if (this.currentPack?.tiers?.length) return this.currentPack.tiers;
      return [{ tierName: "G1", rate: 5 }];
    },
    selectedPack() {
      return this.packs.find((p) => p.id === this.selectedPackId) || null;
    },
    currentPackPrice() {
      return Number(this.currentPack?.price) || 0;
    },
    effectiveBalance() {
      return this.liveBalance !== null ? this.liveBalance : this.walletBalance;
    },
    canBuy() {
      if (!this.selectedPackId) return false;
      if (this.isCurrentPack(this.selectedPackId)) return false;
      if (this.isLowerPack(this.selectedPack)) return false;
      if (this.selectedPack?.price > 0 && this.effectiveBalance < this.selectedPack.price) return false;
      return true;
    },
  },

  methods: {
    theme(name) { return packTheme(name); },
    isCurrentPack(id) { return this.currentPack?.id === id; },
    isLowerPack(p) {
      if (!p || !this.currentPack) return false;
      if (this.isCurrentPack(p.id)) return false;
      return Number(p.price) < this.currentPackPrice;
    },
    onPackClick(p) {
      if (this.isLowerPack(p)) return;
      this.selectedPackId = p.id;
    },

    async openDialog() {
      this.dialog = true;
      this.selectedPackId = this.currentPack?.id || null;
      this.liveBalance = null;

      // ดึงยอดเงินสดล่าสุดจาก DB และ packs พร้อมกัน
      this.fetchingBalance = true;
      const [walletRes] = await Promise.allSettled([
        WalletService.getMyWallet(this.role),
        !this.packs.length
          ? (async () => {
              this.loadingPacks = true;
              try {
                const r = await getPublicPacksApi(this.role);
                this.packs = r.packs || [];
              } catch {
                this.packs = [];
              } finally {
                this.loadingPacks = false;
              }
            })()
          : Promise.resolve(),
      ]);
      if (walletRes.status === "fulfilled") {
        this.liveBalance = walletRes.value?.data?.topUp?.balance ?? this.walletBalance;
      }
      this.fetchingBalance = false;
    },

    async confirmBuy() {
      if (!this.canBuy || !this.selectedPackId) return;
      const pack = this.selectedPack;
      const msg = pack.price === 0
        ? `ต้องการเปลี่ยนเป็นแพ็ค "${pack.name}" ใช่หรือไม่?`
        : `ต้องการซื้อแพ็ค "${pack.name}" ในราคา ฿${pack.price.toLocaleString()} ใช่หรือไม่?`;

      const result = await swalAlert.Confirm(msg);
      if (!result.isConfirmed) return;

      this.buying = true;
      try {
        await buyCommissionPackApi(this.selectedPackId, this.role);
        // ดึงยอดใหม่จาก DB หลังซื้อสำเร็จ
        try {
          const res = await WalletService.getMyWallet(this.role);
          this.liveBalance = res.data?.topUp?.balance ?? this.liveBalance;
        } catch { /* ถ้าดึงไม่ได้ก็ใช้ค่าเดิม */ }
        this.$emit("wallet-changed", this.liveBalance ?? this.walletBalance);
        this.$emit("pack-changed", pack);
        await swalAlert.Success(`อัปเกรดเป็น ${pack.name} สำเร็จ`);
        this.dialog = false;
      } catch (err) {
        swalAlert.Error("ไม่สำเร็จ", err?.response?.data?.message || "ไม่สามารถซื้อแพ็คได้");
      } finally {
        this.buying = false;
      }
    },
  },
};
</script>

<style scoped>
/* ─── Wrapper ─────────────────────────────────── */
.cps-wrap { margin-bottom: 20px; }

/* ─── Hero Card ───────────────────────────────── */
.cps-hero {
  position: relative;
  border-radius: 20px;
  padding: 22px 24px 20px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0,0,0,0.08);
}

/* Decorative blobs */
.cps-blob {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
}
.cps-blob--1 {
  width: 200px; height: 200px;
  top: -60px; right: -40px;
}
.cps-blob--2 {
  width: 120px; height: 120px;
  bottom: -40px; left: 30px;
}

/* Top row */
.cps-hero__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
  position: relative;
}

.cps-hero__badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: rgba(255,255,255,0.6);
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 11px;
  font-weight: 700;
  color: #374151;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  backdrop-filter: blur(4px);
}

.cps-upgrade-btn {
  border-radius: 20px !important;
  font-size: 12px !important;
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;
  backdrop-filter: blur(4px);
  padding: 0 14px !important;
}

/* Main area */
.cps-hero__main {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 18px;
  position: relative;
}

.cps-hero__icon-ring {
  width: 64px;
  height: 64px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 16px rgba(0,0,0,0.10);
}

.cps-hero__name {
  font-size: 26px;
  font-weight: 900;
  letter-spacing: -0.5px;
  line-height: 1.1;
}

.cps-hero__desc {
  font-size: 12px;
  font-weight: 500;
  margin-top: 4px;
  opacity: 0.75;
}

/* Tier pills */
.cps-hero__tiers {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  position: relative;
}

.cps-tier-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border-radius: 10px;
  padding: 5px 12px;
  font-size: 13px;
  backdrop-filter: blur(4px);
}

.cps-tier-pill__gen {
  font-weight: 700;
  color: #374151;
}

.cps-tier-pill__sep {
  font-weight: 900;
  font-size: 14px;
}

.cps-tier-pill__rate {
  font-weight: 800;
  font-size: 14px;
}

/* ─── Dialog ──────────────────────────────────── */
.cpd-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 22px 16px;
}

.cpd-head__icon {
  width: 38px; height: 38px;
  background: #fff7ed;
  border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}

.cpd-head__title {
  font-size: 16px;
  font-weight: 800;
  color: #111827;
}

.cpd-head__sub {
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
}

/* Pack card in dialog */
.cpd-pack-card {
  border: 2px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px 16px;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.1s;
  background: #fff;
  height: 100%;
}

.cpd-pack-card:hover {
  border-color: #fdba74;
  box-shadow: 0 4px 16px rgba(244,129,31,0.12);
  transform: translateY(-1px);
}

.cpd-pack-card--active {
  border-color: #F4811F;
  background: #fff7ed;
}

.cpd-pack-card--selected {
  border-color: #F4811F;
  box-shadow: 0 0 0 3px rgba(244,129,31,0.18);
}

.cpd-pack-card--locked {
  opacity: 0.55;
  cursor: not-allowed;
  background: #f8fafc;
}
.cpd-pack-card--locked:hover {
  border-color: #e2e8f0;
  box-shadow: none;
  transform: none;
}

.cpd-pack-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.cpd-pack-icon {
  width: 32px; height: 32px;
  border-radius: 9px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}

.cpd-pack-name  { font-size: 14px; font-weight: 800; color: #0f172a; }
.cpd-pack-price { font-size: 13px; font-weight: 800; }
.cpd-pack-desc  { font-size: 11px; color: #6b7280; margin-bottom: 10px; line-height: 1.5; }

.cpd-pack-tiers { display: flex; flex-wrap: wrap; gap: 5px; }

.cpd-tier-chip {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  border: 1px solid transparent;
  border-radius: 7px;
  padding: 3px 8px;
  font-size: 11px;
}

/* Dialog footer */
.cpd-footer {
  padding: 12px 20px 16px;
  display: flex;
  align-items: center;
}

.cpd-footer__balance {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #475569;
}

.cpd-footer__dot {
  margin: 0 6px;
  color: #cbd5e1;
}

/* ─── Responsive ──────────────────────────────── */
@media (max-width: 600px) {
  .cps-hero { padding: 18px 18px 16px; border-radius: 16px; }
  .cps-hero__name { font-size: 22px; }
  .cps-hero__icon-ring { width: 52px; height: 52px; border-radius: 14px; }
}
</style>
